/**
 * Composable for managing user activity logging
 * Handles creating, retrieving, and updating log entries
 */
import { ref, toRaw } from 'vue';
import { API } from '@/constants/urls';

/**
 * Create a logger management composable
 * 
 * @param {string} apiUrl - The API URL
 * @param {string} apiToken - The API token for authentication
 * @returns {Object} - Logger management methods and state
 */
export function useLogger(apiUrl = API.BASE, apiToken = API.TOKEN) {
  // Logger data containing user activity and avatars
  const logger = ref(null);
  
  /**
   * Create a new log entry
   * 
   * @param {string} action - The action being logged
   * @param {string} log_txt - The log message
   */
  const createLog = (action, log_txt) => {
    if (!logger.value || !logger.value.logs) return;
    logger.value.logs.push({ action, log: log_txt });
  };
  
  /**
   * Get logger data for a specific user
   * 
   * @param {string} user_id - The user ID
   * @returns {Promise<Object>} - The logger data
   */
  const getLogger = async (user_id) => {
    try {
      const response = await fetch(`${apiUrl}/plugin-logs/${user_id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Get logger response status:', response.status, response.ok);
      
      // If logger doesn't exist, create a new one
      if (!response.ok) {
        return await createLogger(user_id);
      } else {
        const data = await response.json();
        logger.value = data.data; // This is now a reactive object
        console.log('Retrieved logger data:', logger.value);
        return logger.value;
      }
    } catch (error) {
      console.error('Error getting logger data:', error.message);
      return null;
    }
  };
  
  /**
   * Update logger data for a specific user
   * 
   * @param {string} user_id - The user ID
   * @returns {Promise<Object>} - The updated logger data
   */
  const updateLogger = async (user_id) => {
    try {
      // Convert from reactive to plain object
      const rawLogger = toRaw(this.logger);
  
      // Only pick fields your API allows. For example:
      const payload = JSON.stringify({
        avaturn_user_id: rawLogger.avaturn_user_id || user_id,
        logs: rawLogger.logs || [],
        avatars: rawLogger.avatars || [],
      });
  
      const response = await fetch(`${process.env.VUE_APP_API_URL}/plugin-logs/${user_id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
        body: payload,
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      // Refresh your logger after success
      const data = await response.json();
      await this.getLogger(user_id);
      console.log('update logger result', data);
  
    } catch (error) {
      console.error('Error updating logger:', error);
    }
  };
  
  /**
   * Create a new logger for a user
   * 
   * @param {string} user_id - The user ID
   * @returns {Promise<Object>} - The created logger data
   */
  const createLogger = async (user_id) => {
    try {
      const response = await fetch(`${apiUrl}/plugin-logs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          avaturn_user_id: user_id
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      logger.value = data.data;
      console.log('New logger created:', data);
      return logger.value;
    } catch (error) {
      console.error('Error creating logger:', error.message);
      return null;
    }
  };
  
  return {
    logger,
    createLog,
    getLogger,
    updateLogger,
    createLogger
  };
}
